import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
}

interface PerformanceKra {
  id: number;
  position_id: number;
  position: PositionOption | null;
  name: string;
  weight: string;
}

interface KraForm {
  position_id: string | number;
  name: string;
  weight: string;
}

const emptyForm: KraForm = { position_id: '', name: '', weight: '' };

export function PerformanceKras() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<PerformanceKra[] | null>(null);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [editing, setEditing] = useState<'new' | PerformanceKra | null>(null);
  const [form, setForm] = useState<KraForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<PerformanceKra[]>('/performance-kras').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: PerformanceKra) => {
    setForm({ position_id: item.position_id, name: item.name, weight: item.weight });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof KraForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/performance-kras', form);
      else if (editing) await api.put(`/performance-kras/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: PerformanceKra) => {
    if (
      !(await confirmDelete(
        `Delete the KRA "${item.name}" for "${item.position?.position_title}"? Its KPIs will be ungrouped, not deleted.`
      ))
    )
      return;
    try {
      await api.delete(`/performance-kras/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Performance Key Result Areas</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>KRA Name</th>
                <th>Weight</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.position?.position_title ?? '—'}</td>
                  <td>{item.name}</td>
                  <td className="muted">{item.weight}%</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted center">
                    No Key Result Areas configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add Key Result Area' : 'Edit Key Result Area'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Position *
              <select value={form.position_id} onChange={set('position_id')} required>
                <option value="">Select…</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.position_code} — {p.position_title}
                  </option>
                ))}
              </select>
              {errors.position_id && <small className="error">{errors.position_id[0]}</small>}
            </label>
            <label className="span-2">
              KRA Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Weight (%) *
              <input type="number" step="0.1" min="0" max="100" value={form.weight} onChange={set('weight')} required />
              <small className="muted">
                A KPI grouped under this KRA has its weight normalized within the KRA, not across the whole position.
              </small>
              {errors.weight && <small className="error">{errors.weight[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
